/**
 * Sender merge fields in personal signatures (#955).
 *
 * Composers resolve `{{Sender*}}` fields against whoever sends
 * (`lib/signatureMergeVars.js`), so a signature built from them shows each
 * sender their own details. A signature built before #955 usually holds its
 * author's details as typed text instead: shared as-is, everyone it is shared
 * with would send the author's name. These helpers detect that case and turn a
 * structured signature's details back into merge fields.
 */

const SENDER_FIELD_RE = /\{\{\s*Sender[A-Za-z]+\s*\}\}/;

/** Field key → merge field, for the details that belong to the sender. */
const FIELD_TOKENS = {
    name: '{{SenderName}}',
    title: '{{SenderTitle}}',
    company: '{{SenderCompany}}',
    email: '{{SenderEmail}}',
    phone: '{{SenderPhone}}',
    linkedin: '{{SenderLinkedIn}}',
    address: '{{SenderAddress}}',
};

/** True when the signature HTML uses at least one `{{Sender…}}` field. */
export function hasSenderFields(html) {
    return SENDER_FIELD_RE.test(html || '');
}

/**
 * The same structured signature with every filled-in sender detail replaced
 * by its merge field, and the photo by `{{SenderPhoto}}`. Empty details stay
 * empty; layout, style, sign-off, logo and non-sender fields (website,
 * booking link, tagline, pronouns) are kept as they are.
 *
 * @param {object|null} data the `signature_data` SignatureEditor emits
 * @returns {object|null} converted data, or null when there is none
 */
export function toSenderFields(data) {
    if (!data || !data.fields) return null;
    const fields = { ...data.fields };
    Object.entries(FIELD_TOKENS).forEach(([key, token]) => {
        if (typeof fields[key] === 'string' && fields[key].trim()) fields[key] = token;
    });
    return {
        ...data,
        fields,
        photo_url: data.photo_url ? '{{SenderPhoto}}' : data.photo_url,
    };
}
